import { sbEditable } from '@storyblok/storyblok-editable'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { containerDefault } from '../../lib/styles/Container'
import { typoHeading2 } from '../../lib/styles/Typography'
import { renderRichtext } from '../../lib/utils/renderRichtext'
import { SbAsset } from '../../types'
import { ButtonModule } from '../Button'
import { TextMediaItemBlok } from './types'
import { useParallaxAnimation } from './useParallaxAnimation'

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  width: 100%;

  @media screen and (min-width: 1024px) {
    ${containerDefault}
    flex-direction: row;
    gap: 4rem;
    margin: auto;
  }
`

const ContentWrapper = styled.div`
  flex: 1 1 auto;
  margin-inline: auto;
  max-width: 28rem;
  width: 90vw;

  @media screen and (min-width: 1024px) {
    flex: 1 1 40%;
    margin-inline: 0;
    max-width: none;
    padding-block: 4rem;
    width: auto;
  }
`

const MediaWrapper = styled.div<{
  $backgroundImage?: SbAsset
  $ignoreTheme?: boolean
  $isLarge?: boolean
  $isLeft?: boolean
}>`
  background-attachment: fixed;
  background-color: ${p => (p.$ignoreTheme ? p.theme.colors.black[7] : 'var(--c-theme-7)')};
  background-position: top left;
  border-radius: 1.5rem;
  flex: 1 1 auto;
  margin-inline: auto;
  max-width: 32rem;
  padding: 1rem;
  width: 95vw;

  @media screen and (min-width: 480px) {
    padding: 1.5rem;
  }

  @media screen and (min-width: 1024px) {
    flex: 1 1 60%;
    margin-inline: 0;
    order: ${p => (p.$isLeft ? -1 : 1)};
    padding: 2rem;

    ${p =>
      p.$isLarge &&
      css`
        height: 110vh;
        max-height: 44rem;
      `}
  }
`

const MediaTrack = styled.div`
  height: 100%;
`

const Media = styled.img<{
  hasBackgroundImage?: boolean
}>`
  aspect-ratio: 1/1;
  display: block;
  object-fit: cover;
  width: 100%;
  order: -1;

  @media screen and (min-width: 1024px) {
    aspect-ratio: auto;
    order: 1;
  }
`

const ContentHeadline = styled.h3`
  ${typoHeading2}
  margin-bottom: 1.5rem;
`

const ButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`

interface TextmediaItemProps {
  blok: TextMediaItemBlok
  isLarge?: boolean
  direction?: 'right' | 'alternate'
  index: number
}

export const TextmediaItem = ({ blok, index, direction = 'right', isLarge = true }: TextmediaItemProps) => {
  const mediaRef = React.useRef<HTMLImageElement | null>(null)
  const wrapperRef = React.useRef<HTMLDivElement | null>(null)
  const mediaWrapperRef = React.useRef<HTMLDivElement | null>(null)

  const enableParallax = isLarge

  useParallaxAnimation({
    mediaRef,
    mediaWrapperRef,
    wrapperRef,
    enabled: enableParallax,
  })

  return (
    <Wrapper className={blok.theme ? `theme-${blok.theme}` : undefined} ref={wrapperRef} {...sbEditable(blok)}>
      <ContentWrapper>
        {blok.headline && <ContentHeadline>{blok.headline}</ContentHeadline>}
        <div className="prose">{renderRichtext(blok.content)}</div>
        {blok.button && blok.button.length > 0 && (
          <ButtonWrapper>
            {blok.button.map(button => (
              <ButtonModule key={button._uid} blok={button} />
            ))}
          </ButtonWrapper>
        )}
      </ContentWrapper>
      <MediaWrapper
        style={{ backgroundImage: blok.backgroundImage?.filename && `url(${blok.backgroundImage.filename})` }}
        $backgroundImage={blok.backgroundImage}
        $isLarge={isLarge}
        $isLeft={direction === 'alternate' && index % 2 === 0}
        $ignoreTheme={blok.ignoreTheme}
      >
        <MediaTrack ref={mediaWrapperRef}>
          <Media
            hasBackgroundImage={!!blok.backgroundImage}
            ref={mediaRef}
            src={blok.media.filename}
            alt={blok.media.alt || blok.media.name}
          />
        </MediaTrack>
      </MediaWrapper>
    </Wrapper>
  )
}
